import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import useApiService from '../../services/api'
import type { ActivityV2 } from '../../models/activityV2'

const PAGE_SIZE = 20

interface UseRecommendationActivityResult {
  events: ActivityV2[]
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  error: unknown
  /** Re-run the feed query in place (used by the error-state Retry button). */
  refetch: () => void
}

/**
 * Activity feed for the homepage Action Center panel — a deliberately
 * **isolated** variant of the deal/company `useEventHistoryQuery`.
 *
 * The shared hook reads *and mutates* a global zustand filter store
 * (`useEventHistoryFilterStore`), which is what powers the company + deal detail
 * pages' filter UI. Reusing it here entangles the homepage with those pages
 * (opening this panel used to `clearAllFilters()`, wiping their filters). This
 * hook takes **no filters** and never touches that store, and uses its own
 * (`recommendationActivity`) query key so it shares nothing — not even the RQ
 * cache — with the detail pages.
 *
 * Scope follows the rec (see `resolveActivityScope`): deal-focused recs pass
 * `dealId` (deal-scoped feed, matching the "Deal Activity" header), everything
 * else passes `chainProxyId` (company-scoped). Pass exactly one.
 */
export function useRecommendationActivityQuery({
  dealId,
  chainProxyId,
}: {
  dealId?: number
  chainProxyId?: number
}): UseRecommendationActivityResult {
  const api = useApiService()
  const isDeal = dealId != null
  const entityId = dealId ?? chainProxyId

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['recommendationActivity', isDeal ? 'deal' : 'company', entityId],
    queryFn: ({ pageParam }) => {
      const params = { page_size: PAGE_SIZE, page: pageParam }
      return isDeal
        ? api.getDealActivityList(dealId!, params)
        : api.getCompanyActivityList(chainProxyId!, params)
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      return totalFetched < lastPage.count ? allPages.length + 1 : undefined
    },
    enabled: entityId != null,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    // The pane is lazy-mounted (only when opened), and it's an activity feed —
    // so fetch fresh each time it opens, otherwise a stage change / email you
    // just made wouldn't show until the cache went stale.
    refetchOnMount: 'always',
  })

  // Same content filtering the shared feed applies: drop document events with no
  // field changes, and email UPDATE rows (only new emails — CREATE — are shown).
  const events = useMemo(() => {
    const all = (data?.pages ?? []).flatMap((page) => page.results)
    return all.filter((event) => {
      if (event.content_type?.model === 'document') {
        return (event.field_history_objects?.length ?? 0) > 0
      }
      if (
        event.content_type?.model === 'emailmessage' &&
        event.action_type === 'UPDATE'
      ) {
        return false
      }
      return true
    })
  }, [data?.pages])

  return {
    events,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    isLoading: isFetching && !isFetchingNextPage,
    error,
    refetch,
  }
}
